import { useEffect } from 'react'
import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { LoadDataFromParams } from '../../Utils/url-helpers'
import { SelectedVehicle, UseVehiclesResult } from './vehicle-types'

function useInitPassengersFromQueryParams() {
  const { state, dispatch } = useTravelSearchContext()
  const { queryData } = LoadDataFromParams()

  useEffect(() => {
    if (state.selectedVehicle && !state.selectedPassengers) {
      dispatch({
        type: 'SET_PASSENGERS',
        passengers: {
          adults: queryData.adults ?? 1,
          children: queryData.childrenAges?.length ?? 0,
          childrenAges: queryData.childrenAges ?? [],
          pets: queryData.pets ?? 0,
        },
      })
    }
  }, [state.selectedPassengers, state.selectedVehicle, dispatch, queryData])
}

function useEnforcePassengerLimits(matchedVehicle: any) {
  const { state, dispatch } = useTravelSearchContext()
  const routesApiData = state.routesApiData

  useEffect(() => {
    if (!matchedVehicle || !state.selectedPassengers) return

    const maxPassengers = matchedVehicle.maxNumberOfPassengers ?? 9
    const { children, childrenAges, pets } = state.selectedPassengers
    let adults = state.selectedPassengers.adults ?? 1
    const totalPassengers = adults + children

    if (!adults && routesApiData && routesApiData?.campaignPassengers > 0) {
      adults = routesApiData.campaignPassengers
    }

    if (totalPassengers > maxPassengers) {
      const adjustedAdults = Math.min(adults, maxPassengers)
      const adjustedChildren = Math.max(0, maxPassengers - adjustedAdults)
      const adjustedChildrenAges = childrenAges.slice(0, adjustedChildren)

      dispatch({
        type: 'SET_PASSENGERS',
        passengers: {
          adults: adjustedAdults,
          children: adjustedChildren,
          childrenAges: adjustedChildrenAges,
          pets: pets ?? 0,
        },
      })
    }
  }, [matchedVehicle, state.selectedPassengers, dispatch, routesApiData])
}

export function useVehicles(): UseVehiclesResult {
  const { state, dispatch } = useTravelSearchContext()
  const vehicleTypesData = state.vehicleApiData
  const routesApiData = state.routesApiData
  const { queryData } = LoadDataFromParams()

  const vehicleCode =
    state.selectedVehicle?.code ??
    queryData.vehicleCode ??
    (routesApiData?.defaultToNoVehicle ? 'NCAR' : (vehicleTypesData?.defaultVehicleCode ?? 'NCAR'))
  const matchedVehicle = vehicleTypesData?.vehicleTypes?.find((v) => v.code === vehicleCode)
  const isVehicleValid = (vehicleCode === 'NCAR' && !vehicleTypesData?.isVehicleMandatory) || !!matchedVehicle
  const defaultVehicleCode = routesApiData?.defaultToNoVehicle ? 'NCAR' : (vehicleTypesData?.defaultVehicleCode ?? 'NCAR')
  const validatedVehicleCode = isVehicleValid ? vehicleCode : defaultVehicleCode

  useInitPassengersFromQueryParams()
  useEnforcePassengerLimits(matchedVehicle)

  const selectedPassengers = state.selectedPassengers ?? {
    adults: 1,
    children: 0,
    childrenAges: [],
    pets: 0,
  }

  const selectedVehicle: SelectedVehicle = {
    code: validatedVehicleCode,
    trailer: state.selectedVehicle?.trailer,
    count: validatedVehicleCode === 'NCAR' ? 0 : (state.selectedVehicle?.count ?? 1),
    categoryCode: state.selectedVehicle?.categoryCode,
  }

  const isVehiclesLoading = !state.selectedRoute?.routeCode || !vehicleTypesData || !routesApiData || state.vehicleApiLoading

  // Sync selectedVehicle to global state if it differs
  useEffect(() => {
    const currentCode = state.selectedVehicle?.code
    if (validatedVehicleCode && validatedVehicleCode !== currentCode && !isVehiclesLoading) {
      dispatch({
        type: 'SET_VEHICLE',
        vehicle: selectedVehicle,
      })
    }
  }, [validatedVehicleCode, selectedVehicle, state.selectedVehicle?.code])

  const petsAllowed =
    validatedVehicleCode === 'NCAR' ? (vehicleTypesData?.isPetsAllowedForFootPassenger ?? false) : (matchedVehicle?.petsAllowed ?? true)

  const totalPassengers = selectedPassengers.adults + selectedPassengers.children
  const maxPassengers = matchedVehicle?.maxNumberOfPassengers ?? 9

  return {
    isVehiclesLoading,
    vehicleApiData: vehicleTypesData ?? ({} as any),
    selectedVehicle,
    petsAllowed,
    selectedPassengers,
    numberOfPassengers: totalPassengers,
    maxNumberOfPassengers: maxPassengers,
    multipleVehicles: state.multipleVehiclesChecked,
  }
}
